import Messages from "../model/Messages";
import BubbleType from "../model/BubbleType";
import { isBubbleTypeRichMediaWidget } from "./bubbleviews/controller/BubbleController";
import { Constants, RICH_WIDGET, SYSTEM_STATE, TYPING_STATE } from "../model/Constants";
import Aria from "../util/Aria";

/**
 * Rendering logic for displaying chat bubbles are in this UIRender class
 * @class UIRender
 * @extends Messages
 */
class UIRender extends Messages {
    $app: any;

    groupMsg: any;

    ariaUtil: any;

    transcriptArea: any;

    /**
     *
     * @param {CIApp} $app
     */
    constructor($app) {
        super();
        this.$app = $app;

        /**
         * @type {CIApp.enableMsgGrouping}
         */
        this.groupMsg = $app.enableMsgGrouping;

        this.ariaUtil = new Aria($app);
    }

    /**
     * add New message to the transcript
     * @param {Message} message
     */
    addNewItem(message) {
        if (!message.getMessageText()) {
            return;
        }

        let lastMessageType;
        let lastmsg;
        const messageType = message.getMessageBubble();

        message.setMessageText(
            this.ariaUtil.injectAttributes(message.getMessageText())
        );

        if (this.hasMessages()) {
            lastmsg = this.messages[this.messages.length - 1];
            lastMessageType = lastmsg.getMessageBubble();
        }

        if (lastmsg && lastmsg.getRichData()) {
            /**
             * if Rich widget is a receipt for a customer action and rich widget is configured to replace the original widget,
             * For Ex: Ratings survey. When user selected a rating, a receipt survey is created to replace the original survey
             */

            const hideAfterCustomerMessage = lastmsg.getRichData().hideAfterMessage && messageType === BubbleType.CUSTOMER_MESSAGE && !message.richMediaDisplay;
            const richData = message.getRichData();

            if (richData || hideAfterCustomerMessage) {
                // Check if the new message is a rich widget created after customer action
                
                 const wA = richData?.customerAction ? richData?.receipt.widgetAction : richData?.widgetAction;   
                

                // Check if the rich widget will replace the last Agent generated Rich Media widget
                if ((wA && wA.endsWith(Constants.WIDGET_ACTION_REMOVE)  && 
                    (messageType === BubbleType.CUSTOMER_MESSAGE || 
                    messageType === BubbleType.RICH_MESSAGE_RECEIPT))
                    || hideAfterCustomerMessage
                    ) {
                    // Delete the last rich widget
                    this.delete(this.messages.length - 1);

                    if (messageType === BubbleType.CUSTOMER_MESSAGE) {
                        /*
                         * Attempt group the widget that is about to be rendered with the last displayed message, providedly if it is a
                         * customer message
                         */
                        this.groupPreviousMsg(message);
                    }

                    lastmsg.getRichInstance().clearAll();
                    lastmsg.setRichInstance(null);
                } else if (richData.widgetView == RICH_WIDGET.INLINE) {
                    // Fix to render consecutive rich media message and avoid grouping of inline rich widget message
                    this.add(message);
                    return;
                }
            }
        } else if (lastMessageType === BubbleType.TYPING_MESSAGE) {
            if (messageType === BubbleType.TYPING_MESSAGE) {
                // this.onItemReload(this.messages.length-1, message);
                this.update(this.messages.length - 1, message);
                return;
            }

            this.messages.splice(this.messages.length - 1, 1);
            this.transcriptArea.hideTyping();

            if (messageType !== BubbleType.SYSTEM_MESSAGE) {
                this.groupPreviousMsg(message);
            }
        } else if (lastMessageType === BubbleType.SYSTEM_MESSAGE && !lastmsg.getPersistInTranscriptState()) {
            if (messageType === BubbleType.SYSTEM_MESSAGE) {
                // this.onItemReload(this.messages.length-1, message);
                this.update(this.messages.length - 1, message);
                return;
            }

            this.delete(this.messages.length - 1);
            this.groupPreviousMsg(message);
        }

        if (messageType === BubbleType.TYPING_MESSAGE) {
            if (message.getInternalState() == TYPING_STATE.STOPPED_TYPING) {
                return;
            }
        } else if (lastMessageType === BubbleType.SYSTEM_MESSAGE || messageType === BubbleType.RICH_MESSAGE_RECEIPT) {
            // eslint-disable-line no-empty
        } else if (this.groupMsg && lastMessageType) {
            if (lastMessageType === messageType) {
                this.messages[this.messages.length - 1].setSamePreviousType(true);
            } else if (isBubbleTypeRichMediaWidget(messageType) && lastMessageType === BubbleType.AGENT_MESSAGE && message.getMessageText() && message.getRichData().showMessageText && !message.isEmptyResponse()) {
                message.setMessageBubble(lastMessageType);
                this.renderBubbleInTranscriptArea(message, true);
                message.setMessageBubble(messageType);
                message.setMessageText("");
            }
        }

        this.add(message);
    }

    /**
     * render the message to top of the transcript
     * @param {Message} message
     */
    addNewItemToTop(message) {
        if (!message.getMessageText()) {
            return;
        }

        let lastMessageType;
        let lastmsg;
        const messageType = message.getMessageBubble();

        message.setMessageText(
            this.ariaUtil.injectAttributes(message.getMessageText())
        );

        if (this.hasMessages()) {
            lastmsg = this.messages[0];
            lastMessageType = lastmsg.getMessageBubble();
        }

        if (lastmsg && lastmsg.getRichData()) {
            const rd = message.getRichData();

            if (rd && lastmsg.getRichData().customerAction && messageType != BubbleType.RICH_MESSAGE_RECEIPT) {
                const widgetAction = lastmsg.getRichData().receipt.widgetAction;

                if (widgetAction && widgetAction.endsWith(Constants.WIDGET_ACTION_REMOVE)) {
                    return;
                }
            }
        }

        if (messageType === BubbleType.RICH_MESSAGE_RECEIPT) {
            // eslint-disable-line no-empty
        } else if (this.groupMsg && lastMessageType === messageType) {
            message.setSamePreviousType(true);
        }

        this.set(0, message);
    }

    /**
     * Group the previous and current messages if both are of same type
     * @param {Message} message
     */
    groupPreviousMsg(message) {
        if (this.groupMsg && this.messages.length >= 1) {
            if (this.messages[this.messages.length - 1].getMessageBubble() === message.getMessageBubble()) {
                this.messages[this.messages.length - 1].setSamePreviousType(true);
            }
        }
    }

    /**
     * Delete the last message
     */
    deleteLastItem() {
        this.delete(this.messages.length - 1);
    }

    /**
     * Kicks in the rendering or display of bubble to the bottom of transcript
     * @param {number} index
     */
    onItemAdded(index) {
        const isSamePreviousType = this.groupMsg && index > 0 && this.messages[index - 1].isSamePreviousType();
        this.renderBubbleInTranscriptArea(this.messages[index], isSamePreviousType);
    }

    /**
     * Display bubble in the transcript area
     * @param {message} Message
     * @param {isSamePreviousType} boolean
     */
     renderBubbleInTranscriptArea(message, isSamePreviousType) {
        if (!this.transcriptArea) {
            this.transcriptArea = this.$app.shoutui.getTranscriptEl();
        }

        this.transcriptArea.renderMessageBubbles(message, isSamePreviousType);
    }

    /**
     * Kicks in the rendering or display of bubble at the given position
     * @param {number} index
     * @param {Message} message
     */
    onItemSet(index, message) {
        if (!this.transcriptArea) {
            this.transcriptArea = this.$app.shoutui.getTranscriptEl();
        }

        if (index === 0) {
            this.transcriptArea.renderMessageBubbleToTop(message, this.groupMsg ? message.isSamePreviousType() : false);
        }
    }

    /**
     * Delete the provided message bubbles from transcript window
     * @param {Array.<Message>} msg
     */
    onItemDeleted(msg) {
        this.transcriptArea.deleteMessageBubbles(msg);
    }

    /**
     * Reload the provide message
     * @param {number} index
     * @param {Message} message
     */
    onItemReload(index, message) {
        message.setMessageText(
            this.ariaUtil.injectAttributes(message.getMessageText())
        );
        this.transcriptArea.reloadMessageBubbles(index, message);
    }

    /**
     * return the same type message from the messages array for the given type from behind.
     * @param {BubbleType} type
     * @return {Message|undefined}
     */
    getLastMsgOfType(type) {
        for (let i = this.messages.length - 1; i >= 0; i--) {
            if (type == this.messages[i].getMessageBubble()) {
                return this.messages[i];
            }
        }
    }

    /**
     *
     * @param {Message|undefined} message
     * @param {boolean} state
     */
    toggleLoadingMsg(message, state) {
        if (!this.transcriptArea) {
            this.transcriptArea = this.$app.shoutui.getTranscriptEl();
        }

        this.transcriptArea.toggleLoadingMessage(message, state);
    }
}

export default UIRender;
